import {
  REPORT_TIME_ZONE,
  type AdminAnalyticsPeriod,
  type AdminAnalyticsResponse,
  type AdminAnalyticsRollup,
  type ReportCategory,
} from '@mds/shared/types/reporting';

import type { BarRow } from '@/components/charts/bar-ranking';
import type { DonutSlice } from '@/components/charts/donut-chart';
import type { TrendGranularity, TrendPoint } from '@/components/charts/trend-line';
import { dateStamp } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';

/**
 * View models for the Analytics screen.
 *
 * Everything here reads `/admin/reports/analytics`, which counts money the
 * business actually recorded. Nothing is re-derived in the browser: a rollup
 * arrives summed, and a total never comes from a page of rows.
 */

export interface RangeOption {
  label: string;
  days: number;
}

/**
 * The windows the range picker offers.
 *
 * The server caps a range at 366 calendar days, so twelve months is the
 * longest preset that can be asked for.
 */
export const RANGES: RangeOption[] = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 3 months', days: 91 },
  { label: 'Last 6 months', days: 182 },
  { label: 'Last 12 months', days: 365 },
];

/** The picker value that opens the two date inputs instead of a preset. */
export const CUSTOM_RANGE = 'custom';

/** The server rejects anything wider — `validReportRange` in the schemas. */
export const MAX_RANGE_DAYS = 366;

export interface DateRange {
  from: string;
  to: string;
}

/** The report's three categories, spelled the way the server labels them. */
export const CATEGORY_OPTIONS: { value: ReportCategory; label: string }[] = [
  { value: 'space_hire', label: 'Space hire' },
  { value: 'desk_package', label: 'Desk packages' },
  { value: 'event', label: 'Events' },
];

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Today where the business is, not where the browser is.
 *
 * Reports are filtered on Beirut calendar dates. Asking the local clock would
 * put a laptop in another timezone a day out at the range edges, which is
 * exactly the UTC/Beirut mix the API docs warn against.
 */
export const businessToday = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: REPORT_TIME_ZONE }).format(new Date());

const utcOf = (dateKey: string) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

/** Calendar arithmetic on a `yyyy-mm-dd` key, done in UTC so no offset creeps in. */
export function shiftDays(dateKey: string, days: number): string {
  const shifted = new Date(utcOf(dateKey) + days * 86_400_000);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(
    shifted.getUTCDate(),
  )}`;
}

/** The inclusive `from`/`to` pair a preset stands for, ending today. */
export function rangeOf(days: number, today = businessToday()): DateRange {
  return { from: shiftDays(today, -(days - 1)), to: today };
}

/** How many calendar days a range covers, both ends included. */
export const spanDays = (range: DateRange) =>
  Math.round((utcOf(range.to) - utcOf(range.from)) / 86_400_000) + 1;

/**
 * Why a typed range cannot be asked for, or null when it can. The rules are
 * the server's, repeated here so the page says so before a request fails.
 */
export function rangeProblem(range: { from: string; to: string }): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(range.from) || !/^\d{4}-\d{2}-\d{2}$/.test(range.to)) {
    return 'Pick both a start and an end date.';
  }
  if (range.to < range.from) return 'The end date is before the start date.';
  if (spanDays(range) > MAX_RANGE_DAYS) return 'A range can cover at most 366 days.';
  return null;
}

/** The window of the same length that ends the day before `range` starts. */
export function previousRange(range: DateRange): DateRange {
  const days = spanDays(range);
  const to = shiftDays(range.from, -1);
  return { from: shiftDays(to, -(days - 1)), to };
}

/** "08/09/26 – 07/10/26", the way a range reads in a note. */
export const rangeLabel = (range: DateRange): string =>
  `${dateStamp(range.from)} – ${dateStamp(range.to)}`;

/**
 * The change from the previous window, for a stat card's note.
 *
 * Null when there is nothing to compare against: a previous period that took
 * nothing cannot be a percentage of anything, and saying "+∞%" helps nobody.
 */
export function deltaLabel(current: number, previous: number | undefined): string | null {
  if (previous === undefined || previous <= 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return 'level with the previous period';
  return `${pct > 0 ? '+' : ''}${pct}% on the previous period`;
}

/**
 * USD for a chart label: exact below $10,000, abbreviated above it.
 *
 * Takes cents, because every amount on the wire is an integer cent and
 * converting early is how a rounding error gets in.
 */
export const money = (cents: number) =>
  cents >= 1_000_000 ? `$${(cents / 100_000).toFixed(1)}k` : formatCents(cents);

/** 8_250 basis points → "82.5%". Whole percentages drop the decimal. */
export const percentOf = (basisPoints: number) =>
  `${(basisPoints / 100).toFixed(basisPoints % 100 === 0 ? 0 : 1)}%`;

/** A ranked row keeps both measures, so a tooltip can show the one not plotted. */
export interface Ranked extends BarRow {
  sales?: number;
  collectedAmountMinor?: number;
}

const toRanked = (r: AdminAnalyticsRollup, by: 'revenue' | 'sales'): Ranked => ({
  key: r.key,
  label: r.label,
  value: by === 'revenue' ? r.collectedAmountMinor : r.saleCount,
  display: by === 'revenue' ? money(r.collectedAmountMinor) : r.saleCount.toLocaleString(),
  detail: [
    by === 'revenue'
      ? `${r.saleCount.toLocaleString()} ${r.saleCount === 1 ? 'sale' : 'sales'}`
      : `${money(r.collectedAmountMinor)} collected`,
    `${money(Math.round(r.collectedAmountMinor / Math.max(r.saleCount, 1)))} per sale`,
  ],
  sales: r.saleCount,
  collectedAmountMinor: r.collectedAmountMinor,
});

/**
 * Turn a server rollup into ranked bars.
 *
 * `by` picks which measure is plotted; the other rides along in the tooltip
 * with the per-sale average, which is the figure a reader reaches for next.
 */
export function rank(rollups: AdminAnalyticsRollup[], by: 'revenue' | 'sales'): Ranked[] {
  return rollups.map((r) => toRanked(r, by)).sort((a, b) => b.value - a.value);
}

const WEEKDAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * The weekday rollup in calendar order, Monday first.
 *
 * A ranking by value would hide the shape of the week; the point of the chart
 * is to see Thursday next to Friday, not above it.
 */
export function byWeekday(rollups: AdminAnalyticsRollup[], by: 'revenue' | 'sales'): Ranked[] {
  return [...rollups]
    .sort((a, b) => WEEKDAY_ORDER.indexOf(a.key) - WEEKDAY_ORDER.indexOf(b.key))
    .map((r) => toRanked(r, by));
}

/** Narrow ranked rows to what the donut needs, dropping the per-measure detail. */
export const toSlices = (rows: Ranked[]): DonutSlice[] =>
  rows.map((r) => ({ key: r.key, label: r.label, value: r.value, display: r.display }));

/** Up to this many days the trend is drawn per day; up to `WEEKLY_UP_TO`, per week. */
const DAILY_UP_TO = 31;
const WEEKLY_UP_TO = 182;

/** True when the bucket starting at `key` has not ended by `to`. */
function isPartial(key: string, granularity: TrendGranularity, to: string): boolean {
  if (granularity === 'month') return key === to.slice(0, 7);
  if (granularity === 'day') return key === to;
  return key <= to && to < shiftDays(key, 7);
}

/**
 * The finest time series the response carries that suits the range.
 *
 * A 30-day window plotted by month is one or two points, which is not a trend.
 * The daily and weekly series are optional on the response until the server
 * adds them, so this falls back to months whenever a finer one is missing.
 */
export function pickSeries(
  data: AdminAnalyticsResponse,
  range: DateRange,
): { granularity: TrendGranularity; points: TrendPoint[] } {
  const span = spanDays(range);
  const to = data.filters.to;
  const periods = (rows: AdminAnalyticsPeriod[], granularity: TrendGranularity) => ({
    granularity,
    points: rows.map((p) => ({
      key: p.period,
      value: p.collectedAmountMinor,
      partial: isPartial(p.period, granularity, to),
    })),
  });
  if (span <= DAILY_UP_TO && data.byDay) return periods(data.byDay, 'day');
  if (span <= WEEKLY_UP_TO && data.byWeek) return periods(data.byWeek, 'week');
  return {
    granularity: 'month',
    points: data.byMonth.map((m) => ({
      key: m.month,
      value: m.collectedAmountMinor,
      partial: isPartial(m.month, 'month', to),
    })),
  };
}
